/**
 * Product API
 *
 * API endpoints for product management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { Product, ProductSearch, SaveProductArgs } from "./product.types";

/**
 * Product API endpoints
 * Provides CRUD operations for product management
 */
export const productApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all products
     * @returns Array of products
     */
    getProducts: build.query<Product[], void>({
      query: () => "/product",
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando produtos...",
          success: "Produtos carregados com sucesso!",
          error: "Erro ao carregar os produtos."
        });
      }
    }),

    /**
     * Get a single product by ID
     * @param id - Product ID
     * @returns Full product details
     */
    getProduct: build.query<Product, Pick<Product, "id">>({
      query: ({ id }) => `/product/${id}`,
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando produto...",
          success: "Produto carregado com sucesso!",
          error: "Erro ao carregar o produto."
        });
      }
    }),

    /**
     * Create or update a product
     * @param payload - Product data (id optional for create, required for update)
     * @returns void
     */
    saveProduct: build.mutation<Omit<SaveProductArgs, "id">, SaveProductArgs>({
      query: (body) => ({
        url: "/product/create",
        method: "POST",
        body
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: args.id ? "Atualizando produto..." : "Criando produto...",
          success: args.id ? "Produto atualizado com sucesso!" : "Produto criado com sucesso!",
          error: args.id ? "Erro ao atualizar o produto." : "Erro ao criar o produto."
        });
      }
    }),

    /**
     * Delete a product
     * @param id - Product ID to delete
     * @returns void
     */
    deleteProduct: build.mutation<void, Pick<Product, "id">>({
      query: ({ id }) => ({
        url: `/product/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo produto...",
          success: "Produto excluído com sucesso!",
          error: "Erro ao excluir o produto."
        });
      }
    }),

    /**
     * Search products by query string
     * @param query - Search query
     * @returns Array of matching products (minimal fields)
     */
    searchProducts: build.query<ProductSearch[], { query: string }>({
      query: ({ query }) => `/product/like/${query}`
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
productApi.enhanceEndpoints({
  addTagTypes: ["Product"],
  endpoints: {
    getProducts: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Product" as const, id })), { type: "Product", id: "LIST" }] : [{ type: "Product", id: "LIST" }])
    },
    getProduct: {
      providesTags: (result, error, { id }) => [{ type: "Product", id }]
    },
    saveProduct: {
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: "Product", id },
              { type: "Product", id: "LIST" }
            ]
          : []
    },
    deleteProduct: {
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: "Product", id },
              { type: "Product", id: "LIST" }
            ]
          : []
    },
    searchProducts: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Product" as const, id })), { type: "Product", id: "LIST" }] : [{ type: "Product", id: "LIST" }])
    }
  }
});
